import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  Play,
  SkipBack,
  SkipForward,
  X,
} from 'lucide-react';
import {
  POOL_BALL_RADIUS,
  POOL_CUE_BALL,
  derivePool,
  describeShot,
  groupOf,
  isGuestId,
  legalTargetGroup,
  shotStartBalls,
  type GameMeta,
  type PoolMove,
  type PoolPoint,
  type PoolState,
} from '@awwyeah/shared';
import { useCurrentPlayer } from '../guestPlayer';
import { createGameMeta, nowIso } from '../gameOps';
import { useGamesRegistry } from '../useGamesRegistry';
import { useGameRoster } from '../useHouseholdRoster';
import { usePoolMoves } from '../usePoolMoves';
import { SideLine } from '../components/SideLine';
import { unlockGameAudio } from '../sound';
import {
  aimToward,
  defaultAim,
  isPlacementSafe,
  nudgeAngle,
  placeCueBall,
  powerFromPull,
  touchesCueBall,
} from './aim';
import { playShot, replayShot, type PoolRenderBall } from './playback';
import { aimPreview, type PoolFrame, type PoolSoundEvent } from './physics';
import {
  ballColor,
  drawTable,
  fitCanvasToDpr,
  isStripe,
  poolLayout,
  toCanvas,
  toTable,
  type PoolLayout,
} from './render';
import { playCueStrike, playTableEvent, unlockPoolAudio } from './sounds';
import { publishRematch } from '../rematch';

/**
 * 8-ball Pool board (WS-17e) — the first physics game. Turn-based, "live but not realtime":
 * you take a whole shot, it lands in the move log as one entry, and the other player's
 * device plays exactly that shot back when they open it.
 *
 * **Where the truth lives.** Nothing on this screen is authoritative. The canvas draws
 * whatever `derivePool(moves)` says (`packages/shared/src/modules/games/pool.ts`), the
 * animation is a local re-simulation, and when a re-simulation disagrees with the shooter's
 * recorded snapshot the snapshot wins and a warning is logged (`playback.ts`'s
 * `replayShot`). A spectator, a player mid-replay and a phone that just came back online
 * all converge on the same table because they all derive it the same way.
 *
 * **The controls** (`aim.ts` has the why, with sources). Aim and power are separate, the way
 * the mobile pool games people actually stick with do it:
 *  - touch anywhere on the table and the cue points at your finger; drag to swing it;
 *  - the fine-tune wheel under the table (and its ‹ › nudges) turns it a hair at a time;
 *  - the power bar beside the table: pull down, let go to shoot; slide back to the top to
 *    cancel. A tap is zero pull — an accidental touch can never take your shot.
 * Ball in hand: the first touch drops the cue ball (drag to slide it), and after that a touch
 * *on* the cue ball picks it up again while a touch anywhere else aims — unambiguous now
 * that aiming no longer starts on the ball. It can't go anywhere illegal: it's held on the
 * felt, out of the pocket mouths and off other balls (`placeCueBall`), with a red ring under
 * your finger when it had to be nudged.
 *
 * **Sitting at the table.** Each player sees their own end at the bottom: the breaker has the
 * head (where the break is played from) nearest them, the other player the foot. A display
 * rotation only (`render.ts`'s `flipped`) — stored coordinates never change.
 *
 * **Theme.** The felt and rails are the object, fixed in every theme (`render.ts` explains
 * why); every piece of chrome around them — header, trays, buttons, status — is semantic
 * tokens. The only theme-derived thing on the table is the current shooter's member colour.
 */

/**
 * Below this the balls are too small to aim at, so the page scrolls instead of shrinking the
 * table further. In practice that only bites in landscape on a short phone: the app's fixed
 * chrome (header + bottom nav) leaves ~190px of a 375px-tall viewport, and a 2:1 table in
 * 190px would have 4px balls. Portrait is the orientation this game is designed for —
 * landscape stays usable, it just scrolls.
 */
const MIN_BOARD_HEIGHT = 230;
/** Full power is this much pull on the power bar (or 80% of the bar, if that's shorter). */
const FULL_PULL_PX = 220;

function trayBalls(state: PoolState, group: 'solids' | 'stripes' | null): number[] {
  if (!group) return [];
  return state.pocketed.filter((n) => groupOf(n) === group);
}

/** The pocketed-ball tray — a fact about what's down, never a score. */
function Tray({
  label,
  color,
  balls,
  active,
  testId,
}: {
  label: string;
  color: string;
  balls: number[];
  active: boolean;
  testId: string;
}): JSX.Element {
  return (
    <div
      className="flex min-h-[36px] items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5"
      data-testid={testId}
      data-active={active}
      style={active ? { borderColor: color } : undefined}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        <span className="truncate text-xs font-semibold text-fg">{label}</span>
      </span>
      <span className="flex flex-1 flex-wrap items-center gap-1">
        {balls.length === 0 ? (
          <span className="text-[11px] text-fg-3">nothing down yet</span>
        ) : (
          balls.map((n) => (
            <span
              key={n}
              data-testid="pool-tray-ball"
              className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-[#161616] shadow-[inset_-2px_-2px_3px_rgba(0,0,0,0.35)]"
              style={{
                background: isStripe(n)
                  ? `linear-gradient(#f7f3e8 26%, ${ballColor(n)} 26%, ${ballColor(n)} 74%, #f7f3e8 74%)`
                  : ballColor(n),
                color: isStripe(n) ? '#161616' : '#f7f3e8',
              }}
            >
              {n}
            </span>
          ))
        )}
      </span>
    </div>
  );
}

export function PoolBoardPage({ meta }: { meta: GameMeta }): JSX.Element {
  const navigate = useNavigate();
  // `useCurrentPlayer`, not `useUser`: on `/play/:token` the player is a guest with no
  // household session, and this board reads identity itself rather than taking it as a prop.
  const user = useCurrentPlayer();
  const roster = useGameRoster(meta.guestNames);
  const registries = useGamesRegistry();
  const { movesArray, moves, state } = usePoolMoves(meta.id, meta.playerIds);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<PoolLayout | null>(null);

  /** Balls being animated right now — a ref, not state, so a 60 Hz playback never re-renders
   * React; it just repaints the canvas. */
  const animBallsRef = useRef<PoolRenderBall[] | null>(null);
  const playbackRef = useRef<{ cancel: () => void } | null>(null);
  /** How many moves have already been shown as an animation. `null` until the move log has
   * loaded, so opening an old game doesn't replay the whole match at you. */
  const animatedThroughRef = useRef<number | null>(null);
  /** Which gesture the current table touch is: moving the cue ball, or aiming. */
  const gestureRef = useRef<'place' | 'aim' | null>(null);
  /** Power-bar drag: where the finger touched down, and the pull that means full power. */
  const pullRef = useRef<{ startY: number; fullPx: number } | null>(null);
  /** Fine-tune wheel drag: the last x seen. */
  const wheelRef = useRef<number | null>(null);

  const [animating, setAnimating] = useState(false);
  /** The player's aim, remembered per turn (keyed to the move count) so a new turn starts
   * fresh on the default aim instead of wherever the last shot pointed. */
  const [aimState, setAimState] = useState<{ forMove: number; angle: number } | null>(null);
  const [pull, setPull] = useState(0);
  const [wheelOffset, setWheelOffset] = useState(0);
  const [placement, setPlacement] = useState<PoolPoint | null>(null);
  /** Where the finger is while placing, when the ball couldn't go exactly there. */
  const [placementFinger, setPlacementFinger] = useState<PoolPoint | null>(null);
  const [repositioning, setRepositioning] = useState(false);
  const [replayIndex, setReplayIndex] = useState<number | null>(null);
  const [diverged, setDiverged] = useState(false);
  const [boardHeight, setBoardHeight] = useState(360);

  const isPlayer = user ? meta.playerIds.includes(user.id) : false;
  // Rematch writes a new entry to the `games` registry, the one doc a guest may not open
  // (WS-23); members only.
  const canRematch = isPlayer && user !== null && !user.isGuest;
  // An early-eight foul auto-spawns its own successor (below) — the manual button is only for
  // a clean win, where nothing happens on its own.
  const showManualRematch = state.winReason !== 'opponent-lost-eight';
  const meIndex = user ? meta.playerIds.indexOf(user.id) : -1;
  /** Seat 1 sits at the foot end — see "Sitting at the table" above. */
  const flipped = meIndex === 1;

  /** What the canvas is showing: a replayed prefix while scrubbing, otherwise live. */
  const shownState = useMemo(
    () => (replayIndex === null ? state : derivePool(moves.slice(0, replayIndex), meta.playerIds)),
    [replayIndex, state, moves, meta.playerIds],
  );

  const finished = shownState.winner !== null || state.winner !== null;
  const myTurn = isPlayer && state.turn !== null && state.turn === meIndex && !finished;
  const shooterIndex = state.turn ?? state.winner ?? 0;
  const turnColor = roster.getMember(meta.playerIds[shooterIndex] ?? '').color;

  const cueBall = shownState.balls.find((b) => b.n === POOL_CUE_BALL);
  const needsPlacement = myTurn && state.ballInHand && replayIndex === null;
  /** The cue ball hasn't been put down yet (or is being moved) — the next touch places it. */
  const placing = needsPlacement && (placement === null || repositioning);
  const cueAt: PoolPoint | null = needsPlacement ? placement : (cueBall ?? null);
  const placementOk =
    !needsPlacement || (placement !== null && isPlacementSafe(state.balls, placement));

  const defaultAngle = useMemo(() => {
    if (!cueAt || state.turn === null) return 0;
    return defaultAim(state.balls, cueAt, legalTargetGroup(state, state.turn));
  }, [cueAt, state]);
  const angle = aimState?.forMove === moves.length ? aimState.angle : defaultAngle;
  const setAngle = (next: number): void => setAimState({ forMove: moves.length, angle: next });

  const canAim = myTurn && !animating && replayIndex === null && cueAt !== null;
  const canShoot = canAim && placementOk;

  // ---- painting -----------------------------------------------------------------------
  const paint = useCallback((): void => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const cssWidth = wrap.clientWidth;
    const cssHeight = wrap.clientHeight;
    if (cssWidth < 2 || cssHeight < 2) return;
    const ctx = fitCanvasToDpr(canvas, cssWidth, cssHeight);
    if (!ctx) return;
    const layout = poolLayout(cssWidth, cssHeight, flipped);
    layoutRef.current = layout;

    const anim = animBallsRef.current;
    const balls: PoolRenderBall[] =
      anim ??
      shownState.balls
        .filter((b) => b.n !== POOL_CUE_BALL || !needsPlacement)
        .map((b) => ({ ...b, scale: 1 }));

    if (!anim && needsPlacement && placement)
      balls.push({ ...placement, n: POOL_CUE_BALL, scale: 1 });

    const showAim = !anim && canAim && cueAt !== null && gestureRef.current !== 'place';
    drawTable(ctx, layout, {
      balls,
      turnColor,
      highlightCue: !anim && !finished && replayIndex === null && !showAim,
      aim:
        showAim && cueAt
          ? {
              from: cueAt,
              angle,
              preview: aimPreview(shownState.balls, cueAt, angle),
              pull,
            }
          : undefined,
      placement:
        !anim && needsPlacement && placement && (placing || gestureRef.current === 'place')
          ? { at: placement, finger: placementFinger, legal: placementFinger === null }
          : undefined,
    });
  }, [
    shownState,
    angle,
    pull,
    placement,
    placementFinger,
    placing,
    needsPlacement,
    cueAt,
    canAim,
    turnColor,
    finished,
    replayIndex,
    flipped,
  ]);

  useLayoutEffect(() => {
    paint();
  }, [paint]);

  // Board size: as tall as the space actually left below it, never taller than a full
  // portrait table would need. Measured rather than guessed at from a viewport fraction —
  // the app header, the PWA install prompt and the bottom nav all move the board's top edge
  // around, and a fixed fraction pushes the near rail (the end you shoot from) off screen
  // exactly when it matters. Orientation isn't decided here at all: `poolLayout` reads the
  // measured box, so rotating a phone needs a re-measure and nothing else.
  const reserveBelow = isPlayer ? 206 : 150;
  useEffect(() => {
    const measure = (): void => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const width = wrap.clientWidth;
      const top = wrap.getBoundingClientRect().top;
      const viewport = typeof window === 'undefined' ? 720 : window.innerHeight;
      // Reserve for the near player's tray, the fine-tune wheel, the hint and the bottom nav.
      const available = viewport - top - reserveBelow;
      setBoardHeight(Math.round(Math.max(MIN_BOARD_HEIGHT, Math.min(width * 1.885, available))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (wrapRef.current) observer.observe(wrapRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [reserveBelow]);

  useEffect(() => {
    paint();
  }, [boardHeight, paint]);

  // ---- shot animation -----------------------------------------------------------------
  const runPlayback = useCallback(
    (frames: readonly PoolFrame[], events: readonly PoolSoundEvent[]): void => {
      playbackRef.current?.cancel();
      setAnimating(true);
      playbackRef.current = playShot(
        frames,
        (balls) => {
          animBallsRef.current = balls;
          paint();
        },
        () => {
          animBallsRef.current = null;
          playbackRef.current = null;
          setAnimating(false);
          paint();
        },
        { events, onEvent: playTableEvent },
      );
    },
    [paint],
  );

  /** Re-simulate move `index` from the log and animate it — the same code path for "the
   * opponent's shot just arrived", "I opened the game and hadn't seen their shot yet" and
   * "watch the replay". */
  const animateMove = useCallback(
    (index: number): void => {
      const move = moves[index];
      if (!move) return;
      const before = derivePool(moves.slice(0, index), meta.playerIds);
      const start = shotStartBalls(before, move.cueBallPlacement);
      const sim = replayShot(start, { angle: move.angle, power: move.power }, move.outcome);
      if (sim.diverged) setDiverged(true);
      playCueStrike(move.power);
      runPlayback(sim.frames, sim.events);
    },
    [moves, meta.playerIds, runPlayback],
  );

  // New shots arriving (mine are animated at shoot time; this catches the opponent's, and
  // the "I opened the game and their shot is waiting" case).
  useEffect(() => {
    if (replayIndex !== null) return;
    if (animatedThroughRef.current === null) {
      if (moves.length === 0) return;
      const last = moves[moves.length - 1];
      animatedThroughRef.current = last?.shooterId === user?.id ? moves.length : moves.length - 1;
    }
    if (animatedThroughRef.current >= moves.length) {
      animatedThroughRef.current = moves.length;
      return;
    }
    const index = moves.length - 1; // if several arrived at once, show the latest
    animatedThroughRef.current = moves.length;
    animateMove(index);
  }, [moves, user?.id, replayIndex, animateMove]);

  useEffect(() => () => playbackRef.current?.cancel(), []);

  // ---- the table: aim, and ball in hand -------------------------------------------------
  const pointerOnCanvas = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ): { at: PoolPoint; px: { cx: number; cy: number } } | null => {
    const canvas = canvasRef.current;
    const layout = layoutRef.current;
    if (!canvas || !layout) return null;
    const rect = canvas.getBoundingClientRect();
    const px = { cx: event.clientX - rect.left, cy: event.clientY - rect.top };
    return { at: toTable(layout, px.cx, px.cy), px };
  };

  const placeAt = (finger: PoolPoint): void => {
    const placed = placeCueBall(state.balls, finger);
    setPlacement(placed.at);
    setPlacementFinger(placed.legal ? null : finger);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    unlockGameAudio();
    unlockPoolAudio();
    if (!myTurn || animating || replayIndex !== null) return;
    const hit = pointerOnCanvas(event);
    const layout = layoutRef.current;
    if (!hit || !layout) return;
    event.currentTarget.setPointerCapture(event.pointerId);

    const onCue =
      needsPlacement &&
      placement !== null &&
      touchesCueBall(hit.px, toCanvas(layout, placement), POOL_BALL_RADIUS * layout.scale);
    if (placing || onCue) {
      gestureRef.current = 'place';
      setRepositioning(true);
      placeAt(hit.at);
      return;
    }
    if (!cueAt) return;
    gestureRef.current = 'aim';
    const next = aimToward(cueAt, hit.at);
    if (next !== null) setAngle(next);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!myTurn || animating || !gestureRef.current) return;
    const hit = pointerOnCanvas(event);
    if (!hit) return;
    if (gestureRef.current === 'place') {
      placeAt(hit.at);
      return;
    }
    if (!cueAt) return;
    const next = aimToward(cueAt, hit.at);
    if (next !== null) setAngle(next);
  };

  /** Lifting a finger off the table never shoots — only the power bar does. */
  const onPointerEnd = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (gestureRef.current === 'place') {
      setRepositioning(false);
      setPlacementFinger(null);
    }
    gestureRef.current = null;
    paint();
  };

  // ---- the shot -----------------------------------------------------------------------
  const shoot = (shotAngle: number, power: number): void => {
    if (!user || !canShoot) return;
    const start = shotStartBalls(state, placement ?? undefined);
    const sim = replayShot(start, { angle: shotAngle, power });
    const move: PoolMove = {
      shooterId: user.id,
      angle: shotAngle,
      power,
      outcome: sim.outcome,
      at: nowIso(),
      ...(state.ballInHand && placement ? { cueBallPlacement: placement } : {}),
    };
    movesArray.push([move]);
    animatedThroughRef.current = movesArray.length;
    setPlacement(null);
    setPlacementFinger(null);
    setRepositioning(false);
    playCueStrike(power);
    runPlayback(sim.frames, sim.events);
  };

  // Power bar: pull down from wherever you touch it; let go to shoot, or slide back up to
  // the start and let go to cancel. A pointercancel (the OS took the touch) never shoots.
  const onPowerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    unlockGameAudio();
    unlockPoolAudio();
    if (!canShoot) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const track = event.currentTarget.getBoundingClientRect().height;
    pullRef.current = { startY: event.clientY, fullPx: Math.min(FULL_PULL_PX, track * 0.8) };
    setPull(0);
  };
  const onPowerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = pullRef.current;
    if (!drag) return;
    setPull(powerFromPull(event.clientY - drag.startY, drag.fullPx).power);
  };
  const onPowerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = pullRef.current;
    pullRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setPull(0);
    if (!drag) return;
    const { power, cancel } = powerFromPull(event.clientY - drag.startY, drag.fullPx);
    if (!cancel) shoot(angle, power);
  };
  const onPowerCancel = (): void => {
    pullRef.current = null;
    setPull(0);
  };
  const onPowerKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!canShoot) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 0.05 : -0.05;
      setPull((p) => Math.min(1, Math.max(0, p + step)));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const { cancel } = powerFromPull(pull, 1);
      if (!cancel) shoot(angle, pull);
      setPull(0);
    } else if (event.key === 'Escape') {
      setPull(0);
    }
  };

  // Fine-tune wheel: drag sideways to turn the aim a fraction of a degree per pixel.
  const onWheelDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!canAim) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    wheelRef.current = event.clientX;
  };
  const onWheelMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const last = wheelRef.current;
    if (last === null) return;
    const dx = event.clientX - last;
    wheelRef.current = event.clientX;
    if (dx === 0) return;
    turnAim(dx);
  };
  const onWheelEnd = (): void => {
    wheelRef.current = null;
  };
  /** Functional update: several pointermoves can land between renders. */
  const turnAim = (px: number): void => {
    const forMove = moves.length;
    setAimState((prev) => ({
      forMove,
      angle: nudgeAngle(prev?.forMove === forMove ? prev.angle : defaultAngle, px),
    }));
    setWheelOffset((o) => o + px);
  };
  const nudge = (px: number): void => {
    if (canAim) turnAim(px);
  };
  const onWheelKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      nudge(event.key === 'ArrowRight' ? 4 : -4);
    }
  };

  // ---- finishing ----------------------------------------------------------------------
  // Mirror the decided game onto the registry so the Apps tab's quiet Finished section picks
  // it up. Players only — a spectator's client never writes anything. An early-eight foul
  // isn't handled here — it's not a "wait for a manual Rematch tap" ending, so the effect
  // below writes its `done` (with `nextGameId` alongside it) itself.
  useEffect(() => {
    if (!user || !isPlayer) return;
    if (meta.status === 'done' || state.winner === null) return;
    if (state.winReason === 'opponent-lost-eight') return;
    const at = nowIso();
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      winnerId: meta.playerIds[state.winner],
      finishedAt: at,
      updatedBy: user.id,
      updatedAt: at,
    });
  }, [meta, user, isPlayer, registries.map, state.winner, state.winReason]);

  // An early-eight foul (winReason `'opponent-lost-eight'`) doesn't end the *match*, just the
  // rack — a fresh game starts on its own rather than waiting on the manual Rematch button
  // below. Exactly one client creates it, so two devices watching the same ending don't each
  // mint their own successor: the shooter who committed the foul, if they're a signed-in
  // member, otherwise the other player. A guest never creates — WS-23 guests can't open the
  // `games` registry at all, so this effect is a no-op (never throws) on a guest client.
  useEffect(() => {
    if (!user || user.isGuest || !isPlayer) return;
    if (state.winner === null || state.winReason !== 'opponent-lost-eight') return;
    if (meta.status === 'done' || meta.nextGameId) return;
    const shooterSeat = state.lastShot?.shooter ?? null;
    if (shooterSeat === null) return;
    const shooterIsGuest = isGuestId(meta.playerIds[shooterSeat] ?? '');
    const creatorSeat = shooterIsGuest ? (shooterSeat === 0 ? 1 : 0) : shooterSeat;
    if (meIndex !== creatorSeat) return;
    const opponentId = meta.playerIds.find((id) => id !== user.id) ?? meta.playerIds[1] ?? '';
    const fresh = createGameMeta({
      kind: meta.kind,
      starterId: user.id,
      starterName: roster.getMember(user.id).name,
      opponentId,
      opponentName: roster.getMember(opponentId).name,
    });
    const at = nowIso();
    publishRematch(registries.map, fresh, meta);
    registries.map.set(meta.id, {
      ...meta,
      status: 'done',
      winnerId: meta.playerIds[state.winner],
      finishedAt: at,
      nextGameId: fresh.id,
      updatedBy: user.id,
      updatedAt: at,
    });
  }, [
    meta,
    user,
    isPlayer,
    meIndex,
    registries.map,
    state.winner,
    state.winReason,
    state.lastShot,
    roster,
  ]);

  // Follows a finished game to its auto-spawned successor once `nextGameId` shows up on the
  // registry entry — both players' clients get here this way, not just the one that created
  // it. Guarded with a ref rather than relying on the route change to unmount this page: React
  // Router doesn't remount on a path param change alone, so without it a slow navigate could
  // fire twice.
  const followedNextGameRef = useRef(false);
  useEffect(() => {
    if (!isPlayer || !meta.nextGameId || followedNextGameRef.current) return;
    followedNextGameRef.current = true;
    navigate(`/games/${meta.nextGameId}`);
  }, [isPlayer, meta.nextGameId, navigate]);

  const rematch = (): void => {
    if (!user) return;
    const opponentId = meta.playerIds.find((id) => id !== user.id) ?? meta.playerIds[1] ?? '';
    const fresh = createGameMeta({
      kind: meta.kind,
      starterId: user.id,
      starterName: roster.getMember(user.id).name,
      opponentId,
      opponentName: roster.getMember(opponentId).name,
    });
    publishRematch(registries.map, fresh, meta);
    navigate(`/games/${fresh.id}`);
  };

  // ---- replay -------------------------------------------------------------------------
  const stopReplay = (): void => {
    playbackRef.current?.cancel();
    animBallsRef.current = null;
    setAnimating(false);
    setReplayIndex(null);
  };
  const showShot = (index: number): void => {
    if (index < 0 || index >= moves.length) return;
    // The board sits on the *post*-shot state; the animation on top of it is what shows the
    // shot happening, and when it ends the underlying state is already the right one.
    setReplayIndex(index + 1);
    animateMove(index);
  };

  if (!user) return <></>;

  const opponentId = meta.playerIds.find((id) => id !== user.id) ?? meta.playerIds[1] ?? '';
  const opponent = roster.getMember(opponentId);
  // Your own tray sits at your end of the table (the bottom); spectators see seat 0 there.
  const bottomSeat: 0 | 1 = meIndex === 1 ? 1 : 0;
  const topSeat: 0 | 1 = bottomSeat === 0 ? 1 : 0;
  const topPlayer = roster.getMember(meta.playerIds[topSeat] ?? '');
  const bottomPlayer = roster.getMember(meta.playerIds[bottomSeat] ?? '');

  const statusLine = (): string => {
    if (state.winner !== null) {
      const winnerId = meta.playerIds[state.winner];
      if (!isPlayer) return `${roster.getMember(winnerId ?? '').name} won`;
      return winnerId === user.id ? 'You won' : `${opponent.name} won`;
    }
    if (state.turn === null) return '';
    const turnId = meta.playerIds[state.turn];
    if (!isPlayer) return `${roster.getMember(turnId ?? '').name} to shoot`;
    return turnId === user.id ? 'Your shot' : `${opponent.name}'s shot`;
  };

  const groupLabel = (index: 0 | 1): string => {
    const group = state.groups[index];
    return group ? (group === 'solids' ? 'Solids' : 'Stripes') : '';
  };

  const hint = (): string => {
    if (finished) return '';
    if (!myTurn) return 'Waiting on their shot.';
    if (placing) return 'Ball in hand — touch the table to place it.';
    if (pull > 0) return 'Let go to shoot · slide up to cancel.';
    if (needsPlacement) return 'Drag the cue ball to move it · touch to aim.';
    return 'Touch to aim · pull Power down, let go to shoot.';
  };

  const tray = (seat: 0 | 1, player: { name: string; color: string }): JSX.Element => (
    <Tray
      label={`${player.name}${groupLabel(seat) ? ` · ${groupLabel(seat)}` : ''}`}
      color={player.color}
      balls={trayBalls(shownState, state.groups[seat])}
      active={state.turn === seat && !finished}
      testId={`pool-tray-${seat}`}
    />
  );

  const controlsLive = canShoot && !placing;
  const degrees = Math.round(((angle * 180) / Math.PI + 360) % 360);

  return (
    <div className="flex flex-col gap-3 py-4" data-testid="pool-board-page" data-game-kind="pool">
      <header className="flex items-center gap-2">
        <button
          type="button"
          aria-label={user?.isGuest ? 'Back to the lounge' : 'Back to Games'}
          className="flex h-11 w-11 items-center justify-center text-fg-3"
          onClick={() => navigate(user?.isGuest ? '/play/lounge' : '/games')}
        >
          <ChevronLeft size={22} />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-display text-xl tracking-tight text-fg">{meta.title}</h2>
          <p
            className="text-sm text-fg-2"
            data-testid="pool-status-line"
            data-my-turn={myTurn}
            data-finished={finished}
          >
            {!isPlayer && (
              <span className="mr-1 inline-flex items-center gap-1 text-fg-3">
                <Eye size={13} aria-hidden="true" /> Spectating
              </span>
            )}
            {statusLine()}
          </p>
        </div>
        {finished && moves.length > 0 && replayIndex === null && (
          <button
            type="button"
            data-testid="pool-watch-replay"
            onClick={() => showShot(0)}
            className="flex h-11 shrink-0 items-center gap-1.5 rounded-full border border-line-strong px-3 text-sm font-semibold text-fg"
          >
            <Play size={15} aria-hidden="true" /> Replay
          </button>
        )}
      </header>

      {isPlayer && meIndex !== -1 && (
        <SideLine
          color={roster.getMember(user.id).color}
          detail={groupLabel(meIndex as 0 | 1) || 'groups not set yet'}
          turnText={statusLine()}
          turnColor={finished ? null : turnColor}
          myTurn={myTurn}
        />
      )}

      {tray(topSeat, topPlayer)}

      <div className="flex w-full gap-2">
        <div
          ref={wrapRef}
          className="relative min-w-0 flex-1 overflow-hidden rounded-md"
          style={{ height: boardHeight }}
          data-testid="pool-table-wrap"
        >
          <canvas
            ref={canvasRef}
            data-testid="pool-canvas"
            /* Test seams: the settled table in table coordinates, so browser verification can
               aim at a real ball instead of guessing at pixels, plus the current aim and
               orientation. Derived state only — nothing in the app reads them. */
            data-balls={JSON.stringify(shownState.balls)}
            data-aim={canAim ? angle.toFixed(4) : undefined}
            data-flipped={flipped}
            data-placement={placement ? JSON.stringify(placement) : undefined}
            className="absolute inset-0 touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
          />
        </div>

        {isPlayer && (
          <div
            role="slider"
            tabIndex={controlsLive ? 0 : -1}
            aria-label="Shot power — pull down and let go to shoot"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pull * 100)}
            aria-disabled={!controlsLive}
            data-testid="pool-power"
            data-pull={pull.toFixed(3)}
            className={`relative flex w-14 shrink-0 touch-none select-none flex-col items-center rounded-full border border-line bg-raised py-2 transition-opacity ${
              controlsLive ? '' : 'opacity-40'
            }`}
            style={{ height: boardHeight }}
            onPointerDown={onPowerDown}
            onPointerMove={onPowerMove}
            onPointerUp={onPowerUp}
            onPointerCancel={onPowerCancel}
            onKeyDown={onPowerKey}
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-fg-3">
              Power
            </span>
            <div className="relative mt-2 w-3 flex-1 overflow-hidden rounded-full border border-line bg-surface">
              <div
                className="absolute inset-x-0 top-0 rounded-full"
                style={{
                  height: `${pull * 100}%`,
                  background: 'linear-gradient(#4caf6a, #e6b422 60%, #d2462e)',
                }}
              />
            </div>
            {/* The butt of the cue, sliding down as you pull. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 h-11 w-6 -translate-x-1/2 rounded-full shadow"
              style={{
                top: `calc(28px + ${pull} * (100% - 28px - 44px - 30px))`,
                background: 'linear-gradient(90deg, #3d2414, #8a5a34 45%, #5a2f1c)',
                border: '2px solid #e9e1cf',
              }}
            />
            <span className="mt-2 text-[11px] font-semibold tabular-nums text-fg-2">
              {pull > 0 ? `${Math.round(pull * 100)}%` : '↓'}
            </span>
          </div>
        )}
      </div>

      {isPlayer && replayIndex === null && !finished && (
        <div className="flex items-center gap-2" data-testid="pool-fine-tune">
          <button
            type="button"
            aria-label="Nudge aim left"
            data-testid="pool-nudge-left"
            disabled={!canAim}
            onClick={() => nudge(-4)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
          >
            <ChevronsLeft size={18} />
          </button>
          <div
            role="slider"
            tabIndex={canAim ? 0 : -1}
            aria-label="Fine-tune aim — drag sideways"
            aria-valuemin={0}
            aria-valuemax={359}
            aria-valuenow={degrees}
            aria-disabled={!canAim}
            data-testid="pool-aim-wheel"
            className={`h-11 flex-1 touch-none select-none rounded-full border border-line-strong shadow-inner ${
              canAim ? '' : 'opacity-40'
            }`}
            style={{
              backgroundColor: '#3a2618',
              backgroundImage:
                'repeating-linear-gradient(90deg, rgba(255,235,200,0.35) 0 2px, rgba(0,0,0,0) 2px 9px), linear-gradient(rgba(0,0,0,0.45), rgba(255,255,255,0.12) 50%, rgba(0,0,0,0.45))',
              backgroundPosition: `${wheelOffset}px 0, 0 0`,
            }}
            onPointerDown={onWheelDown}
            onPointerMove={onWheelMove}
            onPointerUp={onWheelEnd}
            onPointerCancel={onWheelEnd}
            onKeyDown={onWheelKey}
          />
          <button
            type="button"
            aria-label="Nudge aim right"
            data-testid="pool-nudge-right"
            disabled={!canAim}
            onClick={() => nudge(4)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      )}

      {tray(bottomSeat, bottomPlayer)}

      {replayIndex === null ? (
        <>
          <div className="flex min-h-[20px] items-center gap-3">
            <p className="text-sm text-fg-2" data-testid="pool-hint">
              {animating ? '' : hint()}
            </p>
            {needsPlacement && !placing && !animating && (
              <button
                type="button"
                data-testid="pool-move-cue-ball"
                onClick={() => setRepositioning(true)}
                className="flex min-h-[44px] shrink-0 items-center text-sm font-semibold text-fg underline underline-offset-2"
              >
                Move it
              </button>
            )}
          </div>
          {state.lastShot && !animating && (
            <p className="text-xs text-fg-3" data-testid="pool-last-shot">
              {roster.getMember(meta.playerIds[state.lastShot.shooter] ?? '').name}:{' '}
              {describeShot(state.lastShot)}
            </p>
          )}
        </>
      ) : (
        <div
          className="flex items-center gap-2 rounded-md border border-line bg-raised p-2"
          data-testid="pool-replay-controls"
        >
          <button
            type="button"
            aria-label="Previous shot"
            data-testid="pool-replay-back"
            disabled={replayIndex <= 1}
            onClick={() => showShot(replayIndex - 2)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
          >
            <SkipBack size={16} />
          </button>
          <span className="flex-1 text-center text-xs text-fg-2" data-testid="pool-replay-position">
            Shot {replayIndex} of {moves.length}
          </span>
          <button
            type="button"
            aria-label="Next shot"
            data-testid="pool-replay-forward"
            disabled={replayIndex >= moves.length}
            onClick={() => showShot(replayIndex)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg disabled:opacity-40"
          >
            <SkipForward size={16} />
          </button>
          <button
            type="button"
            aria-label="Exit replay"
            data-testid="pool-replay-exit"
            onClick={stopReplay}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {diverged && (
        <p className="text-xs text-fg-3" data-testid="pool-diverged">
          This shot replayed slightly differently here — showing the shooter&apos;s result.
        </p>
      )}

      {finished && canRematch && replayIndex === null && showManualRematch && (
        <button
          type="button"
          data-testid="rematch-button"
          onClick={rematch}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
        >
          Rematch
        </button>
      )}
    </div>
  );
}
